import type { CustomerOrder } from '../../customerOrdersApi'
import type { CustomerPrivacyRequest } from '../../customerPrivacyApi'
import { buildSupportRequestPath } from '../../helpers/supportRequestPath'
import { toOrderStatusLabel } from '../../helpers/status'
import { toActivityPrivacyLabel } from './dashboardShared'

export type DashboardActivityItem = {
  id: string
  title: string
  meta: string
  timestamp: number
}

export const toDashboardActivityItems = ({
  recentOrders,
  supportRequests,
  privacyRequests,
  accountLastLoginAt,
  summaryLastLoginLabel,
  isItalian,
  language,
}: {
  recentOrders: CustomerOrder[]
  supportRequests: Array<{ id: string; subject: string; createdAt: string; updatedAt: string }>
  privacyRequests: CustomerPrivacyRequest[] | undefined
  accountLastLoginAt: string | null
  summaryLastLoginLabel: string
  isItalian: boolean
  language: 'it' | 'en'
}) => {
  const items: DashboardActivityItem[] = []

  for (const order of recentOrders.slice(0, 4)) {
    items.push({
      id: `order-${order.id}`,
      title: isItalian ? `Ordine ${order.orderNumber}` : `Order ${order.orderNumber}`,
      meta: isItalian
        ? `Stato: ${toOrderStatusLabel(order.status, language)}`
        : `Status: ${toOrderStatusLabel(order.status, language)}`,
      timestamp: Math.max(new Date(order.updatedAt).getTime(), new Date(order.createdAt).getTime()),
    })
  }

  for (const request of supportRequests.slice(0, 2)) {
    items.push({
      id: `support-${request.id}`,
      title: isItalian ? `Ticket ${request.id}` : `Ticket ${request.id}`,
      meta: request.subject || (isItalian ? 'Aggiornamento assistenza' : 'Support update'),
      timestamp: Math.max(new Date(request.updatedAt).getTime(), new Date(request.createdAt).getTime()),
    })
  }

  if (privacyRequests?.length) {
    const latestPrivacy = [...privacyRequests].sort(
      (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
    )[0]

    if (latestPrivacy) {
      items.push({
        id: `privacy-${latestPrivacy.id}`,
        title: isItalian ? 'Richiesta privacy' : 'Privacy request',
        meta: isItalian
          ? `Stato: ${toActivityPrivacyLabel(latestPrivacy.status, language)}`
          : `Status: ${toActivityPrivacyLabel(latestPrivacy.status, language)}`,
        timestamp: new Date(latestPrivacy.createdAt).getTime(),
      })
    }
  }

  if (accountLastLoginAt) {
    items.push({
      id: 'last-login',
      title: isItalian ? 'Ultimo accesso account' : 'Latest account sign in',
      meta: summaryLastLoginLabel,
      timestamp: new Date(accountLastLoginAt).getTime(),
    })
  }

  return items
    .map((item) => ({
      ...item,
      timestamp: Number.isFinite(item.timestamp) ? item.timestamp : 0,
    }))
    .sort((left, right) => right.timestamp - left.timestamp)
    .slice(0, 7)
}

export const buildDashboardSupportPath = (order: CustomerOrder, toLocalizedPath: (path: string) => string) =>
  toLocalizedPath(buildSupportRequestPath(order))
